import { Fragment, useId, useMemo, useState } from "react";
import classNames from "classnames";
import type { SxProps, Theme } from "@mui/material/styles";

import Icons from "assets";
import { ActionsMenu, MenuItem } from "ui/actions-menu";
import { SidebarWorkspace } from "./sidebar-menu.types";
import { useSidebar } from "./sidebar-context";
import { SIDEBAR_PX_COLLAPSED, SIDEBAR_PX_EXPANDED } from "./sidebar-menu.classes";

export type SidebarWorkspaceSwitcherProps = {
  workspaces: SidebarWorkspace[];
  selectedWorkspaceId?: string;
  onWorkspaceSelect: (workspaceId: string) => void;
  placeholder?: string;
  emptyMessage?: string;
};

const WORKSPACE_MENU_SX: SxProps<Theme> = { maxHeight: 400 };

const getInitials = (value: string) =>
  value
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? "")
    .join("");

const SidebarWorkspaceSwitcher = ({
  workspaces,
  selectedWorkspaceId,
  onWorkspaceSelect,
  placeholder,
  emptyMessage,
}: SidebarWorkspaceSwitcherProps) => {
  const { isCollapsed } = useSidebar();
  const [isOpen, setIsOpen] = useState(false);
  const menuId = useId();
  const menuDomId = `sidebar-workspace-menu-${menuId}`;
  const hasWorkspaces = workspaces.length > 0;
  const effectiveSelectedWorkspaceId = selectedWorkspaceId ?? workspaces[0]?.id;

  const handleMenuOpen = () => setIsOpen(true);
  const handleMenuClose = () => setIsOpen(false);

  const selectedWorkspace = useMemo(
    () =>
      effectiveSelectedWorkspaceId
        ? workspaces.find((workspace) => workspace.id === effectiveSelectedWorkspaceId)
        : undefined,
    [workspaces, effectiveSelectedWorkspaceId],
  );

  return (
    <div
      className={classNames(
        "py-2 transition-all duration-200",
        isCollapsed ? SIDEBAR_PX_COLLAPSED : SIDEBAR_PX_EXPANDED,
      )}>
      <ActionsMenu
        itemName="workspace switcher"
        open={isOpen}
        onOpen={handleMenuOpen}
        onClose={handleMenuClose}
        placement="right-start"
        offsetTop={isCollapsed ? 20 : 24}
        menuId={menuDomId}
        sx={{ width: "100%", justifyContent: isCollapsed ? "center" : "flex-start" }}
        menuSx={WORKSPACE_MENU_SX}
        trigger={
          <button
            type="button"
            disabled={!hasWorkspaces}
            aria-haspopup="menu"
            aria-expanded={isOpen}
            aria-controls={menuDomId}
            className={classNames(
              "flex h-10 w-full items-center rounded-md border border-gray-200 bg-white transition-colors disabled:cursor-not-allowed disabled:text-gray-500 focus:outline-none focus:border-aqua-500 focus:shadow-input focus-visible:ring-2 focus-visible:ring-aqua-600 focus-visible:ring-offset-2",
              {
                "gap-3 px-3 pr-2 justify-between": !isCollapsed,
                "w-10 flex-shrink-0 justify-center": isCollapsed,
                "hover:bg-gray-50 hover:border-zinc-300": hasWorkspaces && !isOpen,
                "border-aqua-500 shadow-input": isOpen,
              },
            )}>
            {isCollapsed ? (
              <span className="inline-flex items-center justify-center text-gray-900">
                {selectedWorkspace?.icon ?? getInitials(selectedWorkspace?.name ?? "")}
              </span>
            ) : (
              <>
                <span
                  className="truncate text-base font-normal leading-6 text-gray-900"
                  title={selectedWorkspace?.name}>
                  {selectedWorkspace?.name ?? placeholder}
                </span>
                {hasWorkspaces && <Icons.Move className="size-4 text-gray-900" />}
              </>
            )}
          </button>
        }>
        {workspaces.map((workspace) => (
          <Fragment key={workspace.id}>
            <MenuItem
              label={workspace.name}
              isSelected={workspace.id === effectiveSelectedWorkspaceId}
              onClick={() => {
                onWorkspaceSelect(workspace.id);
                setIsOpen(false);
              }}
            />
          </Fragment>
        ))}
        {workspaces.length === 0 && (
          <div className="px-3 py-4 text-center text-sm text-gray-500">{emptyMessage}</div>
        )}
      </ActionsMenu>
    </div>
  );
};

export default SidebarWorkspaceSwitcher;
